export interface AIFieldContext {
  fieldType: string;
  fieldDescription: string;
  significance: string;
  commonRejectionReasons: string[];
  interviewQuestions: string[];
  finalPrompt: string;
  helpText: string;
  bestPractices: string[];
  warningFlags: string[];
  expectedLength: 'brief' | 'moderate' | 'detailed';
  requiresSpecificity: boolean;
}

export const AI_FIELD_CONTEXTS: Record<string, AIFieldContext> = {
  'passport-card-explanation': {
    fieldType: 'passport-card-explanation',
    fieldDescription: 'Explanation for uncertainty about U.S. passport card possession',
    significance: 'FSOs need complete documentation of all U.S. passport documents. Uncertainty or vague responses trigger additional verification requirements.',
    commonRejectionReasons: [
      'Vague or incomplete explanations',
      'Failure to check with family or records',
      'Not addressing why uncertainty exists',
      'Missing timeline information'
    ],
    interviewQuestions: [
      "Let me help you provide a comprehensive explanation. What do you remember about applying for or receiving any U.S. passport documents, and have you ever traveled internationally?",
      "Do you recall visiting a passport office, post office, or other location for passport services? Are there family members or records that might help clarify your passport history?",
      "Have you checked with parents, family members, or personal records that might contain passport information? What specific steps have you taken to verify your passport history?",
      "Based on our discussion, I'll now generate a comprehensive explanation for your form."
    ],
    finalPrompt: "Based on my reflection on this question, I am uncertain about my U.S. passport card status for the following reasons:",
    helpText: "Use the AI Assistant to help craft a comprehensive explanation that addresses the intent of this question.",
    bestPractices: [
      'Acknowledge the uncertainty directly',
      'Explain specific steps taken to verify',
      'Include timeline of efforts to clarify',
      'Show due diligence in checking records'
    ],
    warningFlags: [
      'Simply saying "I don\'t know"',
      'Not mentioning any verification efforts',
      'Vague timeline references',
      'Failing to check with family/records'
    ],
    expectedLength: 'moderate',
    requiresSpecificity: true
  },
  
  'financial-judgment-actions': {
    fieldType: 'financial-judgment-actions',
    fieldDescription: 'Actions taken to pay financial judgment in full',
    significance: 'Financial responsibility is critical for security clearances. FSOs need to see proactive efforts and a clear plan for resolution.',
    commonRejectionReasons: [
      'No documented payment efforts',
      'Vague or incomplete action plans',
      'Failure to contact creditors or courts',
      'No timeline for resolution',
      'Ignoring the judgment completely'
    ],
    interviewQuestions: [
      "I'll help you provide a comprehensive explanation of your actions to pay this judgment. What specific steps have you taken so far, including any payments made or arrangements negotiated?",
      "Have you contacted the court, creditor, or attorneys about payment plans? What challenges have you faced and how are you addressing them?",
      "What is your current financial situation and timeline for resolving this judgment? Do you have documentation of your efforts?",
      "Based on our discussion, I'll now generate a comprehensive explanation of your actions."
    ],
    finalPrompt: "I have taken the following actions to pay this judgment in full:",
    helpText: "Use the AI Assistant to help provide a comprehensive explanation of your actions to pay the judgment.",
    bestPractices: [
      'Document all contact attempts with creditors',
      'Show proactive communication efforts',
      'Provide specific timelines and amounts',
      'Demonstrate financial responsibility',
      'Include payment arrangements or plans'
    ],
    warningFlags: [
      'Claiming ignorance of the judgment',
      'No documented efforts to pay',
      'Vague promises without timelines',
      'Blaming others without taking responsibility',
      'No current payment plan or arrangements'
    ],
    expectedLength: 'detailed',
    requiresSpecificity: true
  },
  
  'federal-service-disciplinary': {
    fieldType: 'federal-service-disciplinary',
    fieldDescription: 'Explanation of disciplinary actions received during federal employment',
    significance: 'Employment conduct history is heavily scrutinized for security clearances. FSOs need to understand the nature, severity, and your response to disciplinary actions.',
    commonRejectionReasons: [
      'Minimizing or denying responsibility',
      'Failing to show lessons learned',
      'Incomplete disclosure of circumstances',
      'No demonstration of changed behavior',
      'Blaming others without accountability'
    ],
    interviewQuestions: [
      "I'll help you explain your disciplinary actions. What type of disciplinary action did you receive and what were the circumstances that led to it?",
      "How did you respond to the disciplinary action at the time, and what steps have you taken since then to prevent similar issues and learn from the experience?",
      "What specific lessons did you learn from this experience, and how has it changed your approach to workplace conduct?",
      "Based on our discussion, I'll now generate a comprehensive explanation."
    ],
    finalPrompt: "Regarding the disciplinary action I received during my federal employment:",
    helpText: "Use the AI Assistant to help provide a comprehensive explanation about your disciplinary actions.",
    bestPractices: [
      'Take full responsibility for actions',
      'Explain circumstances without making excuses',
      'Show specific lessons learned',
      'Demonstrate changed behavior/attitudes',
      'Provide context without minimizing severity'
    ],
    warningFlags: [
      'Blaming supervisors or coworkers',
      'Minimizing the seriousness',
      'No acknowledgment of wrongdoing',
      'Vague or incomplete explanations',
      'No evidence of learning or growth'
    ],
    expectedLength: 'detailed',
    requiresSpecificity: true
  },

  'federal-service-departure': {
    fieldType: 'federal-service-departure',
    fieldDescription: 'Explanation for leaving federal employment',
    significance: 'Employment departure reasons are carefully evaluated for patterns of misconduct, reliability issues, or termination for cause.',
    commonRejectionReasons: [
      'Vague or evasive explanations',
      'Hiding involuntary termination',
      'Failure to disclose performance issues',
      'Inconsistent information with records',
      'Not addressing negative circumstances'
    ],
    interviewQuestions: [
      "I'll help you provide a comprehensive explanation for why you left your federal employment. What was the primary reason for your departure and the circumstances surrounding it?",
      "Was your departure voluntary or involuntary? How did you handle the transition of your responsibilities and maintain professional relationships?",
      "Were there any performance issues, conflicts, or other factors that influenced your decision to leave? How did you address these professionally?",
      "Based on our discussion, I'll now generate a comprehensive explanation."
    ],
    finalPrompt: "I left my federal employment for the following reasons:",
    helpText: "Use the AI Assistant to help provide a comprehensive explanation for why you left your federal employment.",
    bestPractices: [
      'Be completely honest about circumstances',
      'Distinguish between voluntary and involuntary departure',
      'Address any performance or conduct issues directly',
      'Show professional handling of departure',
      'Provide verifiable information'
    ],
    warningFlags: [
      'Claiming voluntary departure when fired',
      'Omitting performance problems',
      'Vague explanations that raise questions',
      'Inconsistency with employment records',
      'Not addressing known issues'
    ],
    expectedLength: 'moderate',
    requiresSpecificity: true
  }
};

export const getContextForField = (fieldType: string): AIFieldContext => {
  return AI_FIELD_CONTEXTS[fieldType] || AI_FIELD_CONTEXTS['passport-card-explanation'];
};